import { Renderer2 } from "@angular/core";
import { Subscription } from "rxjs";
import { SpinButtonService } from "../services/spin-button.service";

export class SpinButtonHandler {
  private spinnerElement?: HTMLElement;
  private successIcon?: HTMLElement;
  private errorIcon?: HTMLElement;
  private subscription?: Subscription;
  private isInitialized = false;
  private buttonElement?: any;

  constructor(
    public element: HTMLElement,
    private renderer: Renderer2,
    private spinButtonService: SpinButtonService,
    private buttonKey: string
  ) { }

  /**
   * Common error handler
   */
  private handleError(error: any, methodName: string): void {
    console.error(`SpinButtonHandler Error in ${methodName}:`, error);
    this.cleanup();
  }

  /**
   * Initialize handler
   */
  initialize() {
    try {
      // Set up button element reference
      this.buttonElement = this.getButtonElement();
      if (!this.buttonElement) return this;

      this.setupStateSubscription();
      return this;
    } catch (error) {
      this.handleError(error, 'initialize');
      return this;
    }
  }

  /**
   * Get button element based on element type
   */
  private getButtonElement() {
    return this.element.tagName === 'BUTTON' ? 
      this.element : 
      this.element.querySelector('button');
  }

  /**
   * Set up state subscription
   */
  private setupStateSubscription(): void {
    try {
      this.subscription = this.spinButtonService.getState(this.buttonKey)
        .subscribe(state => this.handleStateChange(state));
    } catch (error) {
      this.handleError(error, 'setupStateSubscription');
    }
  }

  /**
   * Handle state changes
   */
  private handleStateChange(state: any): void {
    try {
      if (!this.buttonElement) return;

      if (!this.isInitialized) {
        this.createButtonElements();
      }

      this.updateElementsVisibility(state);
    } catch (error) {
      this.handleError(error, 'handleStateChange');
    }
  }

  /**
   * Create button elements
   */
  private createButtonElements(): void {
    try {
      if (this.isInitialized || !this.buttonElement) return;

      this.renderer.addClass(this.buttonElement, 'spin-button');
      
      this.createSpinner();
      this.createStatusIcons();
      this.appendElements();

      this.isInitialized = true;
    } catch (error) {
      this.handleError(error, 'createButtonElements');
    }
  }

  /**
   * Create spinner element
   */
  private createSpinner(): void {
    this.spinnerElement = this.renderer.createElement('span');
    this.renderer.addClass(this.spinnerElement, 'common-btn-spinner');

    // const spinIcon = this.renderer.createElement('i');
    // ['fa', 'fa-refresh', 'fa-spin'].forEach(className => 
    //   this.renderer.addClass(spinIcon, className)
    // );

    // this.renderer.appendChild(this.spinnerElement, spinIcon);
    this.setElementDisplay(this.spinnerElement, false);
  }

  /**
   * Create success and error icons
   */
  private createStatusIcons(): void {
    this.successIcon = this.renderer.createElement('span');
    this.errorIcon = this.renderer.createElement('span');

    this.renderer.addClass(this.successIcon, 'common-btn-success');
    this.renderer.addClass(this.errorIcon, 'common-btn-error');

    this.setElementDisplay(this.successIcon, false);
    this.setElementDisplay(this.errorIcon, false);
  }

  /**
   * Append elements to button
   */
  private appendElements(): void {
    if (!this.buttonElement) return;

    [this.spinnerElement, this.successIcon, this.errorIcon].forEach(element => {
      if (element) this.renderer.appendChild(this.buttonElement!, element);
    });
  }

  /**
   * Update visibility based on state
   */
  private updateElementsVisibility(state: any): void {
    if (!this.buttonElement || !this.isInitialized) return;

    // Update loading state
    if (state.loading) {
      this.setElementDisplay(this.spinnerElement, true);
      this.renderer.setAttribute(this.buttonElement, 'disabled', 'true');
    } else {
      this.setElementDisplay(this.spinnerElement, false);
      this.renderer.removeAttribute(this.buttonElement, 'disabled');
    }

    // Update status icons
    this.setElementDisplay(this.successIcon, state.success);
    this.setElementDisplay(this.errorIcon, state.error);
  }

  /**
   * Set element display
   */
  private setElementDisplay(element: HTMLElement | undefined, show: boolean): void {
    if (element) {
      this.renderer.setStyle(element, 'display', show ? 'inline-block' : 'none');
    }
  }

  /**
   * Clean up resources
   */
  private cleanup(): void {
    this.subscription?.unsubscribe();
    this.spinButtonService.clear(this.buttonKey);
    this.isInitialized = false;
    this.buttonElement = undefined;
  }

  /**
   * Remove elements and cleanup DOM
   */
  private removeElements(): void {
    if (!this.isInitialized) return;

    // Re-query button in case it changed
    const currentButton = this.getButtonElement();
    if (!currentButton) return;

    // Remove spinner and status icons from DOM
    [this.spinnerElement, this.successIcon, this.errorIcon].forEach(element => {
      if (element) {
        try {
          this.renderer.removeChild(currentButton, element);
        } catch (error) {
          console.warn('Error removing element:', error);
        }
      }
    });

    // Re-enable button and remove spin-button class
    this.renderer.removeAttribute(currentButton, 'disabled');
    this.renderer.removeClass(currentButton, 'spin-button');
  }

  /**
   * Destroy handler - cleanup all DOM and subscriptions
   */
  destroy(): void {
    try {
      this.removeElements();
    } catch (error) {
      this.handleError(error, 'destroy');
    } finally {
      this.cleanup();
    }
  }
}